import { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { projects } from "@/data/projects";
import { techLogos } from "@/data/techLogos";
import { ProjectMedia } from "@/components/work/ProjectMedia";
import { StatusPill } from "@/components/work/StatusPill";
import { MediaLightbox } from "@/components/work/MediaLightbox";
import { MosaicGallery } from "@/components/work/MosaicGallery";
import { ServicesHeader } from "@/components/ServicesHeader";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { projectDetailCopy } from "@/lib/siteCopy";
import { localizeProject, localizeProjects } from "@/lib/localizeProjects";

type NavItem = {
  label: string;
  path?: string;
  href?: string;
  id?: string;
  type: "route" | "scroll" | "external";
};

interface ProjectDetailPageProps {
  navItems: NavItem[];
  onMobileMenuOpen?: () => void;
}

// Pulls the first number-ish token out of a result sentence ("Reduced costs by
// 25%." -> "25%") so it can be shown as a big stat. Falls back to null when a
// result is a plain statement with no figure to lead with.
function extractStat(text: string): string | null {
  const match = text.match(/\$?\d[\d,.]*\s?(?:million|billion|thousand|%|[MKB])?\+?/i);
  return match ? match[0].trim() : null;
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

export default function ProjectDetailPage({
  navItems,
  onMobileMenuOpen,
}: ProjectDetailPageProps) {
  const { id } = useParams<{ id: string }>();
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const lang = useSiteLanguage();
  const t = projectDetailCopy[lang];

  const currentIndex = projects.findIndex((p) => p.id === id);
  const rawProject = projects[currentIndex];

  if (!rawProject) {
    return <Navigate to="/work" replace />;
  }

  const project = localizeProject(rawProject, lang);
  const localizedList = localizeProjects(projects, lang);
  const nextProject = localizedList[(currentIndex + 1) % projects.length]!;
  const prevProject =
    localizedList[(currentIndex - 1 + projects.length) % projects.length]!;
  const statusLabel = project.liveUrl ? t.live : t.caseStudy;
  const gallery = project.gallery ?? [];

  return (
    <div className="min-h-screen bg-background">
      <ServicesHeader
        navItems={navItems}
        onMobileMenuOpen={onMobileMenuOpen}
        variant="work"
      />

      <div className="px-4 pb-24 pt-28 md:px-6">
      <div className="mx-auto max-w-5xl">
        
        {/* Title row */}
        <div className="mt-6 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <div className="flex items-center gap-3">
              {project.logo && <img src={project.logo} alt="" className="h-8 w-8 rounded-md object-contain" />}
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {project.category}
              </span>
            </div>
            <h1 className="mt-3 text-4xl font-bold tracking-tight md:text-5xl">{project.title}</h1>
          </div>

          {project.liveUrl && (
            <Button asChild className="h-11 w-fit rounded-lg px-6 font-medium">
              <a href={project.liveUrl} target="_blank" rel="noreferrer">
                {t.visit}
                <ExternalLink className="ml-2 h-4 w-4" />
              </a>
            </Button>
          )}
        </div>

        {/* Hero - framed like a terminal/editor window so the media always
            reads as "a system we shipped" rather than a marketing screenshot */}
        <div className="mt-8 overflow-hidden rounded-2xl border border-border/50 shadow-sm">
          <div className="flex items-center justify-between gap-3 border-b border-border/50 bg-muted/40 px-4 py-2.5">
            <div className="flex gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
              <span className="h-2.5 w-2.5 rounded-full bg-green-400/70" />
            </div>
            <span className="hidden font-mono text-xs text-muted-foreground sm:block">
              {project.id}.case-study
            </span>
            <StatusPill label={statusLabel} color={project.accentColor} />
          </div>
          <div className="aspect-video bg-muted/30">
            <ProjectMedia image={project.image} video={project.video} alt={project.title} autoPlay fit="cover" />
          </div>
        </div>

        {/* Description */}
        <p className="mt-8 max-w-3xl text-lg leading-relaxed text-muted-foreground">{project.description}</p>

        {/* Meta grid */}
        <div className="mt-8 grid grid-cols-2 gap-6 border-y border-border/50 py-8 md:grid-cols-4">
          <div>
            <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{t.industry}</h3>
            <p className="mt-2 font-medium">{project.industry}</p>
          </div>
          <div>
            <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{t.solution}</h3>
            <p className="mt-2 font-medium">{project.solution}</p>
          </div>
          <div>
            <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{t.year}</h3>
            <p className="mt-2 font-medium">{project.year}</p>
          </div>
          <div>
            <h3 className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{t.duration}</h3>
            <p className="mt-2 font-medium">{project.duration || "-"}</p>
          </div>
        </div>

        {/* Challenge / Approach */}
        <div className="mt-12 grid grid-cols-1 gap-10 md:grid-cols-2">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-mono text-sm uppercase tracking-wider text-muted-foreground">{t.challenge}</h2>
            <p className="mt-4 leading-relaxed text-foreground">{project.challenge}</p>
          </motion.div>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h2 className="font-mono text-sm uppercase tracking-wider text-muted-foreground">{t.approach}</h2>
            <p className="mt-4 leading-relaxed text-foreground">{project.approach}</p>
          </motion.div>
        </div>

        {/* Results */}
        <div className="mt-16">
          <h2 className="font-mono text-sm uppercase tracking-wider text-muted-foreground">{t.impact}</h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {project.results.map((result, i) => {
              const stat = extractStat(result);
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="rounded-xl border border-border/50 bg-muted/20 p-5"
                >
                  {stat && (
                    <div className="font-mono text-2xl font-bold" style={{ color: project.accentColor }}>
                      {stat}
                    </div>
                  )}
                  <p className={`text-sm text-muted-foreground ${stat ? "mt-2" : ""}`}>{result}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Tech stack */}
        <div className="mt-16">
          <h2 className="font-mono text-sm uppercase tracking-wider text-muted-foreground">{t.techStack}</h2>
          <div className="mt-6 flex flex-wrap gap-3">
            {project.techStack.map((tech) => {
              const logo = techLogos[tech];
              return (
                <div
                  key={tech}
                  className="flex items-center gap-2 rounded-full border border-border/50 bg-muted/40 px-3.5 py-2"
                >
                  {logo && <img src={logo} alt={tech} className="h-5 w-5 rounded-full bg-white object-contain" />}
                  <span className="text-sm font-medium">{tech}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Gallery */}
        {gallery.length > 0 && (
          <motion.div
            className="mt-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="font-mono text-sm uppercase tracking-wider text-muted-foreground">
                  {t.gallery}
                </h2>
                <p className="mt-2 max-w-lg text-sm text-muted-foreground">
                  {t.gallerySub}
                </p>
              </div>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {String(gallery.length).padStart(2, "0")} {t.assets}
              </span>
            </div>
            <div className="mt-6">
              <MosaicGallery
                items={gallery}
                fallbackImage={project.image}
                accentColor={project.accentColor}
                onItemClick={setLightboxIndex}
              />
            </div>
          </motion.div>
        )}

        <MediaLightbox
          items={gallery}
          activeIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />

        {/* Prev / Next navigation */}
        <div className="mt-20 grid grid-cols-1 gap-4 border-t border-border/50 pt-10 md:grid-cols-2">
          <Link
            to={`/work/${prevProject.id}`}
            className="group flex items-center gap-4 rounded-xl border border-border/50 p-5 transition-colors hover:border-foreground/30 hover:bg-muted/30"
          >
            <ArrowLeft className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:-translate-x-1" />
            <div>
              <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{t.previous}</p>
              <p className="mt-1 font-semibold">{prevProject.title}</p>
            </div>
          </Link>
          <Link
            to={`/work/${nextProject.id}`}
            className="group flex items-center justify-between gap-4 rounded-xl border border-border/50 p-5 text-right transition-colors hover:border-foreground/30 hover:bg-muted/30 md:justify-end"
          >
            <div>
              <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{t.next}</p>
              <p className="mt-1 font-semibold">{nextProject.title}</p>
            </div>
            <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </div>
      </div>
    </div>
  );
}